/**
 * Learning / University API — typed wrappers + TanStack Query cache keys.
 *
 * Mirrors apps/chaos-gateway/src/learning.ts. The gateway already
 * translates snake_case → camelCase for the endpoints below, so callers
 * see idiomatic TS shapes from @chaos/types/learning.
 *
 * Pass-through endpoints (skill-effectiveness, skill-export, marketplace)
 * still arrive in snake_case from the upstream Python service — those
 * wrappers preserve the original shape and surface it to the caller.
 *
 * ── EVERY CALL NAMES A WORKSPACE, AND THE NAME IS NOT TRUSTED ──────────
 * `orgId` is the FIRST argument of every function here, and it is passed in
 * explicitly by the screen rather than read out of the auth store inside this
 * module: the api modules in this repo are pure, and the same reason
 * marketplace.ts::listMarketplaceWorkspaceSkills gives applies unchanged —
 * the gateway puts the value through org-auth.ts and answers 404 for a
 * Workspace the caller is not in, so a stale value in this browser cannot
 * reach another tenant's catalogue.
 *
 * The gateway reads the Workspace from the `?orgId=<uuid>` QUERY parameter on
 * every University route (learning.ts `universityFence`), including the POSTs
 * — which is why it is spliced into the query string here and never into a
 * JSON body. Omitting it is not "no filter": it hands a system founder the
 * legacy super-admin union across every Workspace, which is a screen that
 * proves nothing about isolation. So there is no overload without it.
 *
 * A refused Workspace answers 404 with the same body as a Workspace that does
 * not exist and the same body as a non-uuid. That is deliberate (org-auth.ts
 * `resourceRefusalFor`) and the screens must not try to tell those apart —
 * see components/university/university-chrome.tsx for the one place that
 * decides how a refusal is rendered.
 *
 * ── `orgId` IS ALSO IN EVERY CACHE KEY ────────────────────────────────
 * Otherwise switching Workspace re-serves the previous tenant's rows out of
 * the TanStack cache before (or instead of) a refetch. The gateway would
 * never have sent them a second time; the leak would be entirely in this
 * browser, which is exactly the kind that no server-side test can catch.
 */
import type {
  AbTest,
  AbTestDetail,
  AbTestEvaluation,
  AbTestsList,
  AppendIterationRequest,
  ApproveExampleRequest,
  ApproveExampleResponse,
  ConcludeAbTestRequest,
  CreateAbTestRequest,
  CreateTrainingSessionRequest,
  ExportSkillRequest,
  ExportSkillResponse,
  InstallMarketplaceSkillResponse,
  MarketplaceListing,
  MarketplaceSkillsList,
  PatternClustersResponse,
  PublishToMarketplaceRequest,
  PublishToMarketplaceResponse,
  RedactionPreview,
  SkillCatalogList,
  SkillDetail,
  SkillEffectivenessDetailed,
  SkillVersion,
  TrainingSessionDetail,
  TrainingSessionSummary,
  TrainingSessionsList,
  TunerEdit,
  TunerSummary,
  TunersList,
  UniversitySummary,
} from '@chaos/types/learning';

import { apiJson } from './client';

export interface ListSkillsParams {
  q?: string;
  department?: string;
  status?: string;
  sort?: 'updated' | 'effectiveness' | 'attempts' | 'name';
  limit?: number;
}

export interface ListTrainingSessionsParams {
  tunerId?: string;
  skillId?: string;
  status?: 'planned' | 'active' | 'awaiting_tuner' | 'completed' | 'abandoned';
  limit?: number;
}

function qs(params: Record<string, string | number | undefined>): string {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === '') continue;
    usp.set(k, String(v));
  }
  const s = usp.toString();
  return s ? `?${s}` : '';
}

/**
 * The Workspace claim, spliced into the query string of every request.
 *
 * Written once so no route can quietly forget it, and so `orgId` cannot be
 * shadowed by a caller-supplied param of the same name — the fence reads the
 * query parameter and would take whichever value arrived.
 */
function fenced(
  orgId: string,
  params: Record<string, string | number | boolean | undefined> = {},
): string {
  const raw: Record<string, string | number | undefined> = {};
  for (const [k, v] of Object.entries(params)) {
    if (k === 'orgId') continue;
    if (v === undefined) continue;
    raw[k] = typeof v === 'boolean' ? String(v) : v;
  }
  raw.orgId = orgId;
  return qs(raw);
}

export const learningApi = {
  // ── Skills ──────────────────────────────────────────────────────────
  listSkills(orgId: string, params: ListSkillsParams = {}): Promise<SkillCatalogList> {
    return apiJson<SkillCatalogList>(`/learning/skills${fenced(orgId, { ...params })}`);
  },
  getSkill(orgId: string, keyOrId: string): Promise<SkillDetail> {
    return apiJson<SkillDetail>(
      `/learning/skills/${encodeURIComponent(keyOrId)}${fenced(orgId)}`,
    );
  },
  listSkillVersions(orgId: string, skillId: string): Promise<SkillVersion[]> {
    return apiJson<SkillVersion[]>(
      `/learning/skills/${encodeURIComponent(skillId)}/versions${fenced(orgId)}`,
    );
  },

  // ── Tuners ──────────────────────────────────────────────────────────
  listTuners(orgId: string): Promise<TunersList> {
    return apiJson<TunersList>(`/learning/tuners${fenced(orgId)}`);
  },
  getTuner(orgId: string, tunerId: string): Promise<TunerSummary> {
    return apiJson<TunerSummary>(
      `/learning/tuners/${encodeURIComponent(tunerId)}${fenced(orgId)}`,
    );
  },
  listTunerEdits(orgId: string, tunerId: string, limit: number = 50): Promise<TunerEdit[]> {
    return apiJson<TunerEdit[]>(
      `/learning/tuners/${encodeURIComponent(tunerId)}/edits${fenced(orgId, { limit })}`,
    );
  },

  // ── Training sessions ───────────────────────────────────────────────
  listTrainingSessions(
    orgId: string,
    params: ListTrainingSessionsParams = {},
  ): Promise<TrainingSessionsList> {
    return apiJson<TrainingSessionsList>(
      `/learning/training-sessions${fenced(orgId, { ...params })}`,
    );
  },
  getTrainingSession(orgId: string, sessionId: string): Promise<TrainingSessionDetail> {
    return apiJson<TrainingSessionDetail>(
      `/learning/training-sessions/${encodeURIComponent(sessionId)}${fenced(orgId)}`,
    );
  },
  createTrainingSession(
    orgId: string,
    input: CreateTrainingSessionRequest,
  ): Promise<TrainingSessionSummary> {
    return apiJson<TrainingSessionSummary>(`/learning/training-sessions${fenced(orgId)}`, {
      method: 'POST',
      json: input,
    });
  },
  /**
   * ⚠ NOT IMPLEMENTED IN THE GATEWAY (verified 2026-08-06 against
   * apps/chaos-gateway/src/learning.ts: the file mounts `POST
   * /api/learning/training-sessions` and nothing under `/:sessionId/`).
   *
   * The declaration is kept because the shape is the contract the screen and
   * the future handler have to agree on. Nothing calls it: the session detail
   * screen renders a stated "not available on this instance" panel in place of
   * the append form rather than firing a request whose 404 it would then have
   * to explain away. Wiring a caller before the handler exists would turn a
   * missing feature into a bug report about a broken one.
   */
  appendIteration(
    orgId: string,
    sessionId: string,
    input: AppendIterationRequest,
  ): Promise<TrainingSessionDetail> {
    return apiJson<TrainingSessionDetail>(
      `/learning/training-sessions/${encodeURIComponent(sessionId)}/iterations${fenced(orgId)}`,
      { method: 'POST', json: input },
    );
  },
  /** ⚠ NOT IMPLEMENTED IN THE GATEWAY — see `appendIteration` above. */
  approveExample(
    orgId: string,
    sessionId: string,
    input: ApproveExampleRequest,
  ): Promise<ApproveExampleResponse> {
    return apiJson<ApproveExampleResponse>(
      `/learning/training-sessions/${encodeURIComponent(sessionId)}/approve${fenced(orgId)}`,
      { method: 'POST', json: input },
    );
  },

  // ── Effectiveness (pass-through, snake_case) ────────────────────────
  getEffectiveness(
    orgId: string,
    skillId: string,
    version: string,
  ): Promise<SkillEffectivenessDetailed> {
    return apiJson<SkillEffectivenessDetailed>(
      `/learning/skill-effectiveness/${encodeURIComponent(skillId)}/${encodeURIComponent(
        version,
      )}${fenced(orgId)}`,
    );
  },

  // ── Export (pass-through) ───────────────────────────────────────────
  exportSkill(orgId: string, req: ExportSkillRequest): Promise<ExportSkillResponse> {
    return apiJson<ExportSkillResponse>(`/learning/skill-export${fenced(orgId)}`, {
      method: 'POST',
      json: req,
    });
  },

  // ── Marketplace (pass-through) ──────────────────────────────────────
  listMarketplace(orgId: string, statusFilter?: string): Promise<MarketplaceListing[]> {
    return apiJson<MarketplaceListing[]>(
      `/learning/marketplace${fenced(orgId, { status_filter: statusFilter })}`,
    );
  },

  // ── Summary (gateway-aggregated KPI) ────────────────────────────────
  getSummary(orgId: string): Promise<UniversitySummary> {
    return apiJson<UniversitySummary>(`/learning/summary${fenced(orgId)}`);
  },

  // ── Phase 9 — patterns ──────────────────────────────────────────────
  getPatterns(
    orgId: string,
    params: {
      skillId?: string;
      days?: number;
      limit?: number;
      distanceThreshold?: number;
      minClusterSize?: number;
    } = {},
  ): Promise<PatternClustersResponse> {
    return apiJson<PatternClustersResponse>(`/learning/patterns${fenced(orgId, { ...params })}`);
  },

  // ── Phase 9 — A/B tests ─────────────────────────────────────────────
  listAbTests(orgId: string): Promise<AbTestsList> {
    return apiJson<AbTestsList>(`/learning/ab-tests${fenced(orgId)}`);
  },
  getAbTest(orgId: string, testId: string): Promise<AbTestDetail> {
    return apiJson<AbTestDetail>(
      `/learning/ab-tests/${encodeURIComponent(testId)}${fenced(orgId)}`,
    );
  },
  createAbTest(orgId: string, input: CreateAbTestRequest): Promise<AbTest> {
    return apiJson<AbTest>(`/learning/ab-tests${fenced(orgId)}`, { method: 'POST', json: input });
  },
  concludeAbTest(
    orgId: string,
    testId: string,
    input: ConcludeAbTestRequest,
  ): Promise<{ ok: boolean; evaluation: AbTestEvaluation | null }> {
    return apiJson<{ ok: boolean; evaluation: AbTestEvaluation | null }>(
      `/learning/ab-tests/${encodeURIComponent(testId)}/conclude${fenced(orgId)}`,
      { method: 'POST', json: input },
    );
  },

  // ── Phase 9 — Marketplace browseable surface ────────────────────────
  listMarketplaceSkills(
    orgId: string,
    params: {
      q?: string;
      category?: string;
      sort?: 'updated' | 'installs' | 'effectiveness' | 'name';
      limit?: number;
      includeAllStatuses?: boolean;
    } = {},
  ): Promise<MarketplaceSkillsList> {
    const raw: Record<string, string | number | undefined> = {};
    if (params.q) raw.q = params.q;
    if (params.category) raw.category = params.category;
    if (params.sort) raw.sort = params.sort;
    if (params.limit) raw.limit = params.limit;
    if (params.includeAllStatuses) raw.includeAllStatuses = 'true';
    return apiJson<MarketplaceSkillsList>(`/learning/marketplace/skills${fenced(orgId, raw)}`);
  },
  getRedactionPreview(
    orgId: string,
    skillId: string,
    params: { format?: string; version?: string } = {},
  ): Promise<RedactionPreview> {
    const raw: Record<string, string | number | undefined> = {};
    if (params.format) raw.format = params.format;
    if (params.version) raw.version = params.version;
    return apiJson<RedactionPreview>(
      `/learning/marketplace/skills/${encodeURIComponent(
        skillId,
      )}/redaction-preview${fenced(orgId, raw)}`,
    );
  },
  publishToMarketplace(
    orgId: string,
    input: PublishToMarketplaceRequest,
  ): Promise<PublishToMarketplaceResponse> {
    return apiJson<PublishToMarketplaceResponse>(`/learning/marketplace/publish${fenced(orgId)}`, {
      method: 'POST',
      json: input,
    });
  },
  /**
   * ⚠ DECLARED-STUB ON THE SERVER. The gateway handler exists but answers
   * `501` with `stub: true` — cross-tenant copy needs lineage machinery that
   * is not built. The screen says so instead of showing a spinner that ends in
   * a red toast about a server error.
   */
  installMarketplaceSkill(
    orgId: string,
    skillId: string,
  ): Promise<InstallMarketplaceSkillResponse> {
    return apiJson<InstallMarketplaceSkillResponse>(
      `/learning/marketplace/skills/${encodeURIComponent(skillId)}/install${fenced(orgId)}`,
      { method: 'POST', json: { skillId } },
    );
  },
};

/**
 * Cache keys. `orgId` is the first segment under 'learning' on EVERY key, so
 * two Workspaces can never share a cache entry and switching Workspace cannot
 * paint the previous tenant's rows while the refetch is in flight.
 */
export const learningKeys = {
  all: ['learning'] as const,
  org: (orgId: string) => ['learning', orgId] as const,

  skills: (orgId: string) => ['learning', orgId, 'skills'] as const,
  skillsList: (orgId: string, params: ListSkillsParams) =>
    [...learningKeys.skills(orgId), 'list', params] as const,
  skill: (orgId: string, keyOrId: string) =>
    [...learningKeys.skills(orgId), 'detail', keyOrId] as const,
  skillVersions: (orgId: string, skillId: string) =>
    [...learningKeys.skills(orgId), 'versions', skillId] as const,
  effectiveness: (orgId: string, skillId: string, version: string) =>
    [...learningKeys.skills(orgId), 'effectiveness', skillId, version] as const,

  tuners: (orgId: string) => ['learning', orgId, 'tuners'] as const,
  tunersList: (orgId: string) => [...learningKeys.tuners(orgId), 'list'] as const,
  tuner: (orgId: string, tunerId: string) =>
    [...learningKeys.tuners(orgId), 'detail', tunerId] as const,
  tunerEdits: (orgId: string, tunerId: string, limit?: number) =>
    [...learningKeys.tuners(orgId), 'edits', tunerId, limit ?? null] as const,

  trainingSessions: (orgId: string) => ['learning', orgId, 'training-sessions'] as const,
  trainingSessionsList: (orgId: string, params: ListTrainingSessionsParams) =>
    [...learningKeys.trainingSessions(orgId), 'list', params] as const,
  trainingSession: (orgId: string, sessionId: string) =>
    [...learningKeys.trainingSessions(orgId), 'detail', sessionId] as const,

  marketplace: (orgId: string) => ['learning', orgId, 'marketplace'] as const,
  marketplaceList: (orgId: string, statusFilter?: string) =>
    [...learningKeys.marketplace(orgId), 'list', statusFilter ?? null] as const,

  // Phase 9 — Marketplace browseable list (different endpoint).
  marketplaceSkills: (
    orgId: string,
    params: Record<string, string | number | boolean | undefined> = {},
  ) => [...learningKeys.marketplace(orgId), 'skills', params] as const,
  redactionPreview: (orgId: string, skillId: string, format: string) =>
    [...learningKeys.marketplace(orgId), 'redaction-preview', skillId, format] as const,

  // Phase 9 — Patterns + A/B tests.
  patterns: (orgId: string, params: Record<string, string | number | undefined> = {}) =>
    ['learning', orgId, 'patterns', params] as const,
  abTests: (orgId: string) => ['learning', orgId, 'ab-tests'] as const,
  abTestsList: (orgId: string) => [...learningKeys.abTests(orgId), 'list'] as const,
  abTest: (orgId: string, testId: string) =>
    [...learningKeys.abTests(orgId), 'detail', testId] as const,

  summary: (orgId: string) => ['learning', orgId, 'summary'] as const,
};
